/**
 * Sign-in screen.
 *
 * It renders itself from `auth.settings`, which mirrors `server/config/auth.ts`
 * — switch `method` there and this screen becomes the other form. The visitor
 * is never asked to choose, and no copy of the rules lives on this side.
 */
import { useState } from 'react'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'

import CustomTextField from '@core/components/mui/text-field'
import BlankLayout from '@layouts/blank-layout'
import { getSystemMode } from '@core/utils/server-helpers'
import { CodeInput } from '@components/code-input'
import Logo from '@components/layout/shared/logo'

import { useTRPC } from '@/lib/trpc'
import type { PublicUser } from '@server/domain/user'

export const Route = createFileRoute('/login')({
  validateSearch: z.object({ redirect: z.string().optional() }),
  component: LoginPage
})

function LoginPage() {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { redirect } = Route.useSearch()

  const { data: settings } = useQuery(trpc.auth.settings.queryOptions())

  /**
   * Puts the user straight into the cache the route guard reads. Invalidating
   * would only mark it stale — `ensureQueryData` would still hand the guard
   * the old `null` and bounce us back here.
   */
  const enter = async (user: PublicUser) => {
    queryClient.setQueryData(trpc.auth.me.queryKey(), user)
    await navigate({ to: redirect ?? '/' })
  }

  return (
    <BlankLayout systemMode={getSystemMode()}>
      <div className='flex flex-col justify-center items-center min-bs-[100dvh] p-6'>
        <Card className='flex flex-col is-full sm:is-[450px]'>
          <CardContent className='sm:!p-12'>
            <div className='flex justify-center mbe-6'>
              <Logo />
            </div>
            <div className='flex flex-col gap-1 mbe-6'>
              <Typography variant='h4'>Bem-vindo 👋</Typography>
              <Typography>Entre com sua conta para continuar.</Typography>
            </div>

            {!settings && <Typography color='text.secondary'>Carregando…</Typography>}

            {settings?.method === 'password' && <PasswordForm onSignedIn={enter} />}

            {settings?.method === 'email-code' && (
              <EmailCodeForm codeLength={settings.loginCodeLength} onSignedIn={enter} />
            )}
          </CardContent>
        </Card>
      </div>
    </BlankLayout>
  )
}

/** Identifier + password. */
function PasswordForm({ onSignedIn }: { onSignedIn: (user: PublicUser) => Promise<void> }) {
  const trpc = useTRPC()

  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const signIn = useMutation(trpc.auth.signInWithPassword.mutationOptions({ onSuccess: onSignedIn }))

  return (
    <form
      noValidate
      autoComplete='off'
      className='flex flex-col gap-5'
      onSubmit={event => {
        event.preventDefault()
        signIn.mutate({ identifier, password })
      }}
    >
      <CustomTextField
        autoFocus
        fullWidth
        label='E-mail'
        placeholder='voce@empresa.com'
        value={identifier}
        onChange={event => setIdentifier(event.target.value)}
      />
      <CustomTextField
        fullWidth
        label='Senha'
        placeholder='············'
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={event => setPassword(event.target.value)}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  edge='end'
                  onClick={() => setShowPassword(visible => !visible)}
                  onMouseDown={event => event.preventDefault()}
                >
                  <i className={showPassword ? 'tabler-eye-off' : 'tabler-eye'} />
                </IconButton>
              </InputAdornment>
            )
          }
        }}
      />
      {signIn.error && <Alert severity='error'>{signIn.error.message}</Alert>}
      <Button fullWidth variant='contained' type='submit' disabled={signIn.isPending}>
        {signIn.isPending ? 'Entrando…' : 'Entrar'}
      </Button>
    </form>
  )
}

/** E-mail, then the code that arrives in it. */
function EmailCodeForm({
  codeLength,
  onSignedIn
}: {
  codeLength: number
  onSignedIn: (user: PublicUser) => Promise<void>
}) {
  const trpc = useTRPC()

  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)

  const request = useMutation(
    trpc.auth.requestLoginCode.mutationOptions({
      onSuccess: () => {
        setSentTo(email)
        setCode('')
      }
    })
  )

  const signIn = useMutation(
    trpc.auth.signInWithCode.mutationOptions({
      onSuccess: onSignedIn,
      // A refused code is worth nothing: clearing it puts the person back at
      // the first box instead of making them erase six digits by hand.
      onError: () => setCode('')
    })
  )

  if (!sentTo) {
    return (
      <form
        noValidate
        autoComplete='off'
        className='flex flex-col gap-5'
        onSubmit={event => {
          event.preventDefault()
          request.mutate({ email })
        }}
      >
        <CustomTextField
          autoFocus
          fullWidth
          label='E-mail'
          placeholder='voce@empresa.com'
          value={email}
          onChange={event => setEmail(event.target.value)}
        />
        {request.error && <Alert severity='error'>{request.error.message}</Alert>}
        <Button fullWidth variant='contained' type='submit' disabled={request.isPending}>
          {request.isPending ? 'Enviando…' : 'Receber código'}
        </Button>
      </form>
    )
  }

  return (
    <form
      noValidate
      autoComplete='off'
      className='flex flex-col gap-5'
      onSubmit={event => {
        event.preventDefault()
        signIn.mutate({ email: sentTo, code })
      }}
    >
      <Alert severity='success'>
        Enviamos um código de {codeLength} dígitos para <strong>{sentTo}</strong>.
        {request.data ? ` Ele expira em ${request.data.expiresInMinutes} minutos.` : ''}
      </Alert>
      <CodeInput
        autoFocus
        value={code}
        length={codeLength}
        error={Boolean(signIn.error) && code.length === 0}
        disabled={signIn.isPending}
        onChange={setCode}
        onComplete={filled => {
          // The last digit is the whole answer — waiting for a click after it
          // only makes the person do something they already decided.
          if (!signIn.isPending) signIn.mutate({ email: sentTo, code: filled })
        }}
      />
      {signIn.error && <Alert severity='error'>{signIn.error.message}</Alert>}
      <Button fullWidth variant='contained' type='submit' disabled={signIn.isPending}>
        {signIn.isPending ? 'Entrando…' : 'Entrar'}
      </Button>
      <div className='flex justify-between items-center'>
        <Button size='small' variant='text' onClick={() => setSentTo(null)}>
          Trocar e-mail
        </Button>
        <Button
          size='small'
          variant='text'
          disabled={request.isPending}
          onClick={() => request.mutate({ email: sentTo })}
        >
          Reenviar código
        </Button>
      </div>
    </form>
  )
}
